import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { conversationsAPI } from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import '../styles/Contact.css';

const Contact = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.username || '',
    email: user?.email || '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [successConvId, setSuccessConvId] = useState(null);
  const [error, setError] = useState('');
  const [myMessages, setMyMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchMyMessages = useCallback(async () => {
    if (!user) return;
    setLoadingMessages(true);
    try {
      const response = await api.get('/contact/my-messages');
      setMyMessages(response.data);
    } catch (err) {
      // silently fail
    } finally {
      setLoadingMessages(false);
    }
  }, [user]);

  useEffect(() => {
    // Only fetch old contact messages for non-logged-in users display
    if (!user) return;
    fetchMyMessages();
  }, [fetchMyMessages, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setSuccessConvId(null);

    try {
      if (user) {
        // Utilisateur connecte: creer une conversation support
        const response = await conversationsAPI.startSupport(formData.subject, formData.message);
        setSuccessConvId(response.data.conversationId);
        setSuccess(t('contact.successWithConv'));
      } else {
        // Non connecte: ancien comportement
        await api.post('/contact/send', formData);
        setSuccess(t('contact.successSimple'));
      }
      setFormData({
        name: user?.username || '',
        email: user?.email || '',
        subject: '',
        message: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || t('contact.sendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <div className="contact-header">
          <h1>{t('contact.title')}</h1>
          <p>{t('contact.intro')}</p>
        </div>

        {success && (
          <div className="success-message">
            {success}
            {successConvId && (
              <Link to={`/my-messages?conversation=${successConvId}`} className="success-link">
                {t('contact.viewConversation')}
              </Link>
            )}
          </div>
        )}
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="contact-form">
          {/* Afficher nom/email uniquement si non connecte */}
          {!user && (
            <div className="form-row">
              <div className="form-group">
                <label>{t('contact.nameLabel')}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.namePlaceholder')}
                />
              </div>
              <div className="form-group">
                <label>{t('contact.emailLabel')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label>{t('contact.subjectLabel')}</label>
            <select
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
            >
              <option value="">{t('contact.subjectPlaceholder')}</option>
              <option value="Probleme technique">{t('contact.subjectTechnical')}</option>
              <option value="Question sur un paiement">{t('contact.subjectPayment')}</option>
              <option value="Probleme avec mon compte">{t('contact.subjectAccount')}</option>
              <option value="Signaler une annonce">{t('contact.subjectReport')}</option>
              <option value="Litige acheteur/vendeur">{t('contact.subjectDispute')}</option>
              <option value="Suggestion">{t('contact.subjectSuggestion')}</option>
              <option value="Autre">{t('contact.subjectOther')}</option>
            </select>
          </div>

          <div className="form-group">
            <label>{t('contact.messageLabel')}</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="6"
              placeholder={t('contact.messagePlaceholder')}
            />
          </div>

          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? t('common.sending') : t('contact.sendButton')}
          </button>
        </form>

        {/* Pour les utilisateurs connectes: lien vers la messagerie */}
        {user && (
          <div className="my-messages-section">
            <h2>{t('contact.supportConversations')}</h2>
            <p className="support-redirect-text">
              {t('contact.supportConversationsHint')}
            </p>
            <Link to="/my-messages" className="btn-goto-messages">
              {t('contact.viewMessages')}
            </Link>
          </div>
        )}

        {/* Historique ancien systeme pour les non-connectes (ne s'affiche pas) et pour les connectes qui avaient des anciens messages */}
        {user && myMessages.length > 0 && (
          <div className="my-messages-section">
            <h2>{t('contact.previousMessages')}</h2>
            <div className="my-messages-list">
              {myMessages.map(msg => (
                <div
                  key={msg.id}
                  className={`my-message-card ${msg.status === 'replied' ? 'has-reply' : ''} ${expandedId === msg.id ? 'expanded' : ''}`}
                  onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
                >
                  <div className="my-message-header">
                    <div className="my-message-info">
                      <span className="my-message-subject">{msg.subject}</span>
                      <span className="my-message-date">{formatDateTime(msg.created_at)}</span>
                    </div>
                    <span className={`my-message-status status-${msg.status}`}>
                      {msg.status === 'replied' ? t('contact.replied') : msg.status === 'read' ? t('contact.read') : t('contact.waiting')}
                    </span>
                  </div>

                  {expandedId === msg.id && (
                    <div className="my-message-body">
                      <div className="my-message-content">
                        <span className="msg-label">{t('contact.yourMessage')}</span>
                        <p>{msg.message}</p>
                      </div>
                      {msg.reply && (
                        <div className="my-message-reply">
                          <span className="msg-label">{t('contact.teamReply')}</span>
                          <p>{msg.reply}</p>
                          {msg.replied_at && (
                            <span className="reply-date">{t('contact.repliedOn', { date: formatDateTime(msg.replied_at) })}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="contact-info">
          <h3>{t('contact.otherContact')}</h3>
          <p>{t('contact.emailContact')}</p>
          <p>{t('contact.responseTime')}</p>
        </div>
      </div>
    </div>
  );
};

export default Contact;
